import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Users, Home, Megaphone, Search, ShieldCheck, MapPin } from "lucide-react";
import { Navbar, Footer } from "@/components/Navbar";
import { ProfessionalCard, RentalCard } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const HERO = "https://images.pexels.com/photos/33659330/pexels-photo-33659330.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const navigate = useNavigate();
  const [pros, setPros] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [ads, setAds] = useState([]);

  useEffect(() => {
    api.get("/professionals").then((r) => setPros(r.data.slice(0, 3))).catch(() => {});
    api.get("/rentals").then((r) => setRentals(r.data.slice(0, 3))).catch(() => {});
    api.get("/propagandas").then((r) => setAds(r.data.ads || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#fafaf8]">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-24">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#1b5e3f] bg-[#e8f0ec] px-3 py-1.5 rounded-full">
              <MapPin className="w-3.5 h-3.5" /> Comunidade Brasileira em Portugal
            </span>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1c1917] mt-6 leading-[1.05]">
              Sua rede brasileira em Portugal, <span className="text-[#1b5e3f]">organizada.</span>
            </h1>
            <p className="text-base sm:text-lg text-[#78716c] mt-6 max-w-lg leading-relaxed">
              Encontre profissionais por ramo de atividade, cidade e região. Alugue imóveis com segurança e divulgue os seus serviços para toda a comunidade.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button data-testid="hero-directory-btn" onClick={() => navigate("/diretorio")} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c] px-6 py-6 text-base">
                Explorar diretório <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button data-testid="hero-rentals-btn" onClick={() => navigate("/alugueis")} variant="outline" className="rounded-xl border-2 border-[#1b5e3f] text-[#1b5e3f] hover:bg-[#e8f0ec] px-6 py-6 text-base">
                Ver aluguéis
              </Button>
            </div>
            <div className="flex items-center gap-6 mt-10 text-sm text-[#78716c]">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#1b5e3f]" /> Documentos verificados</div>
              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#1b5e3f]" /> Comunidade ativa</div>
            </div>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgb(27,94,63,0.18)]">
              <img src={HERO} alt="Lisboa" className="w-full h-[460px] object-cover" />
              <div className="absolute inset-0 bg-[#1b5e3f]/30" />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur rounded-2xl p-5 flex items-center gap-3">
                <Search className="w-5 h-5 text-[#1b5e3f]" />
                <span className="text-sm text-[#1c1917] font-medium">Conecte-se com os brasileiros em Portugal</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Users, title: "Diretório profissional", desc: "Cadastre-se por ramo de atividade e seja encontrado por cidade e região.", to: "/diretorio" },
            { icon: Home, title: "Aluguel de imóveis", desc: "Anuncie ou encontre apartamentos, quartos e casas com documentos seguros.", to: "/alugueis" },
            { icon: Megaphone, title: "Propagandas", desc: "Divulgue o seu negócio para toda a comunidade com anúncios em destaque.", to: "/dashboard?tab=ads" },
          ].map((f, i) => (
            <Link to={f.to} key={i} className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 p-7 group">
              <span className="w-12 h-12 rounded-xl bg-[#e8f0ec] flex items-center justify-center group-hover:bg-[#1b5e3f] transition-colors">
                <f.icon className="w-6 h-6 text-[#1b5e3f] group-hover:text-white transition-colors" strokeWidth={1.8} />
              </span>
              <h3 className="font-head text-xl font-semibold text-[#1c1917] mt-5">{f.title}</h3>
              <p className="text-[#78716c] mt-2 leading-relaxed">{f.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Mission */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-[2rem] border border-[#e7e5e4] shadow-sm p-8 sm:p-12 grid lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-3">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#1b5e3f]">Nossa missão</span>
            <h2 className="font-head text-2xl sm:text-3xl font-semibold text-[#1c1917] mt-3 leading-snug">
              A ponte digital entre o Brasil e Portugal
            </h2>
            <p className="text-[#78716c] mt-4 leading-relaxed">
              O <strong className="text-[#1c1917]">Portal Brasil Portugal</strong> nasce para ser a ponte digital entre Brasil e Portugal, conectando pessoas, empresas, oportunidades, serviços e informação de qualidade em um único ecossistema.
            </p>
          </div>
          <div className="lg:col-span-2 grid grid-cols-2 gap-4">
            {[
              { icon: Users, t: "Pessoas" },
              { icon: Megaphone, t: "Empresas" },
              { icon: Home, t: "Serviços" },
              { icon: ShieldCheck, t: "Confiança" },
            ].map((b, i) => (
              <div key={i} className="bg-[#fafaf8] rounded-2xl border border-[#e7e5e4] p-5 flex flex-col items-start gap-2">
                <span className="w-10 h-10 rounded-xl bg-[#e8f0ec] flex items-center justify-center"><b.icon className="w-5 h-5 text-[#1b5e3f]" /></span>
                <span className="font-head font-semibold text-[#1c1917]">{b.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {ads.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#78716c] mb-3">Propagandas em destaque</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ads.slice(0, 3).map((a) => (
              <a key={a.id} href={a.link_url || "#"} target="_blank" rel="noreferrer" data-testid={`ad-${a.id}`} className={`block rounded-2xl border overflow-hidden hover:shadow-md transition-shadow ${a.official ? "border-[#1b5e3f]/30 ring-1 ring-[#1b5e3f]/10 bg-white" : "border-[#fef3c7] bg-[#fffdf7]"}`}>
                {a.image_url && <img src={a.image_url} alt={a.title} className="w-full h-32 object-cover" />}
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase font-bold text-[#d97706]">Anúncio</span>
                    {a.official && <span className="text-[10px] uppercase font-bold text-[#1b5e3f]">Oficial</span>}
                  </div>
                  <h4 className="font-head font-semibold text-[#1c1917]">{a.title}</h4>
                  <p className="text-sm text-[#78716c] line-clamp-2">{a.description}</p>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {pros.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-head text-2xl sm:text-3xl font-semibold text-[#1c1917]">Profissionais em destaque</h2>
            <Link to="/diretorio" className="text-sm font-semibold text-[#f59e0b]">Ver todos →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {pros.map((p) => <ProfessionalCard key={p.id} p={p} />)}
          </div>
        </section>
      )}

      {rentals.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-head text-2xl sm:text-3xl font-semibold text-[#1c1917]">Imóveis recentes</h2>
            <Link to="/alugueis" className="text-sm font-semibold text-[#f59e0b]">Ver todos →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rentals.map((r) => <RentalCard key={r.id} r={r} />)}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-[2rem] bg-[#1b5e3f] px-8 py-14 text-center relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="font-head text-3xl sm:text-4xl font-bold text-white">Crie o seu perfil</h2>
            <p className="text-white/80 mt-3 max-w-xl mx-auto">Promoção de lançamento: assine por apenas <strong className="text-[#f59e0b]">10€</strong> e fique <strong>1 ano inteiro</strong> cadastrado. Depois, mantenha o seu cadastro ativo por 15€/mês.</p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
              <a href="https://buy.stripe.com/eVq3cv0qPb9KaoY6m86Vq02" target="_blank" rel="noreferrer" data-testid="cta-voucher-btn">
                <Button className="rounded-xl bg-[#f59e0b] hover:bg-[#d97706] px-8 py-6 text-base">
                  Ativar Voucher <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <Button data-testid="cta-register-btn" onClick={() => navigate("/login?mode=register")} variant="outline" className="rounded-xl border-2 border-white text-white bg-transparent hover:bg-white/10 px-8 py-6 text-base">
                Criar conta <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <p data-testid="cta-voucher-note" className="text-sm text-white/70 mt-4 max-w-xl mx-auto">
              Ao ativar o voucher, após o pagamento você deverá efetivar o seu cadastro em <strong className="text-white">Criar conta</strong>.
            </p>
          </div>
          <div className="absolute -right-20 -bottom-20 w-72 h-72 rounded-full bg-[#13422c]" />
          <div className="absolute -left-16 -top-16 w-56 h-56 rounded-full bg-[#13422c]/60" />
        </div>
      </section>

      <Footer />
    </div>
  );
}
